// src/context/AuthContext.jsx

import React, { createContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
    const navigate = useNavigate();

    // 1) Inicializar token desde localStorage
    const [token, setToken] = useState(() => {
        return localStorage.getItem('token') || '';
    });

    // 2) Decodificar usuario (role, id, name) desde el token
    const [user, setUser] = useState(() => {
        if (token) {
            try {
                const { id, role, name, adminLite } = jwtDecode(token);
                return { id, role, name, adminLite: adminLite === true };
            } catch {
                return null;
            }
        }
        return null;
    });

    // 3) Sincronizar Axios y localStorage cuando cambie el token
    useEffect(() => {
        if (token) {
            api.defaults.headers.common.Authorization = `Bearer ${token}`;
            localStorage.setItem('token', token);
            // Sincronizar perfil completo desde DB (role puede diferir del JWT si fue cambiado)
            api.get('/auth/me').then(({ data }) => {
                setUser(prev => prev ? {
                    ...prev,
                    role:             data.role,
                    // Admin light: role llega como 'admin' (rol efectivo) y estos
                    // dos dicen que además opera como agente (pausas, etc.).
                    adminLite:        data.adminLite        || false,
                    baseRole:         data.baseRole         || data.role,
                    canAuthorize:     data.canAuthorize     || false,
                    canHandleSupport: data.canHandleSupport || false,
                    isLeader:         data.isLeader         || false,
                    departments:      data.departments      || [],
                    // Acceso a Hechos Operativos (lo resuelve el servidor). Sin
                    // esta línea el sidebar nunca ve el módulo aunque /me lo mande.
                    incidents:        data.incidents        || { hasAccess: false, canReport: false, canSeeLegal: false },
                    // Acceso al Directorio de correos (lista blanca). Sin esta
                    // línea el sidebar nunca ve el módulo aunque /me lo mande.
                    mailDirectory:    data.mailDirectory     || { hasAccess: false },
                } : prev);
            }).catch(() => {});
        } else {
            delete api.defaults.headers.common.Authorization;
            localStorage.removeItem('token');
        }
    }, [token]);

    // Función para hacer login. `redirectTo` lo usa el módulo de salas de
    // juntas (/salas/login), que comparte credenciales pero vive aparte.
    const login = (newToken, redirectTo = '/dashboard') => {
        setToken(newToken);
        const { id, role, name, adminLite } = jwtDecode(newToken);
        setUser({ id, role, name, adminLite: adminLite === true });
        navigate(redirectTo);
    };

    // Función para hacer logout
    const logout = () => {
        setToken('');
        setUser(null);
        navigate('/login');
    };

    return (
        <AuthContext.Provider value={{ token, user, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}
